import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProductsSection = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [visibleCategories, setVisibleCategories] = useState(new Set());
  const observerRef = useRef(null);

  const categoryLabels = {
    'electrode-holders': 'Electrode Holders & Clamps',
    'isi-cutting': 'ISI Cutting Equipment',
    'non-isi-cutting': 'Non-ISI Cutting Equipment',
    'ppe': 'Personal Protective Equipment',
    'drying-ovens': 'Drying Ovens'
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const categoryId = entry.target.dataset.categoryId;
            setVisibleCategories((prev) => new Set([...prev, categoryId]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group products by category
  const groupedProducts = products.reduce((acc, product) => {
    const category = product.category;
    if (!acc[category]) {
      acc[category] = {
        id: category,
        name: categoryLabels[category] || category,
        description: '',
        products: [],
        image: product.image_url || 'https://images.unsplash.com/photo-1563229708-55d0a600b01d'
      };
    }
    acc[category].products.push(product);
    return acc;
  }, {});

  const productCategories = Object.values(groupedProducts);

  return (
    <section id="products" className="py-24 bg-zinc-50 relative overflow-hidden">
      {/* Minimal Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-white via-zinc-50 to-white"></div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Section Header - Minimal and Clean */}
        <div className="text-center mb-20">
          <div className="inline-block mb-3">
            <span className="text-yellow-600 text-xs font-semibold tracking-widest uppercase">Our Products</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-black mb-5 leading-tight">
            Premium <span className="text-yellow-500">Welding Equipment</span>
          </h2>
          <p className="text-zinc-600 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Industrial-grade equipment engineered for performance, durability, and reliability in demanding applications
          </p>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-zinc-500 text-sm">Loading products...</p>
          </div>
        ) : productCategories.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-zinc-200">
            <Package className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-black mb-2">No Products Yet</h3>
            <p className="text-zinc-500 text-sm">Products will appear here once added in admin panel</p>
          </div>
        ) : (
          <>
            {/* Product Categories - ESAB Minimal Art Style */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {productCategories.map((category, index) => {
                const isVisible = visibleCategories.has(category.id);
                
                return (
                  <div 
                    key={category.id}
                    data-category-id={category.id}
                    ref={(el) => {
                      if (el && observerRef.current) {
                        observerRef.current.observe(el);
                      }
                    }}
                    className={`transform transition-all duration-700 ${
                      isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                    }`}
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    <Card 
                      className="bg-white border border-zinc-200 hover:border-yellow-500 hover:shadow-2xl transition-all duration-500 cursor-pointer group overflow-hidden h-full"
                      onClick={() => setSelectedCategory(category)}
                    >
                      {/* Image Container - Large and Clean */}
                      <div className="relative h-72 overflow-hidden bg-zinc-50">
                        {category.products[0]?.image_url ? (
                          <img 
                            src={category.products[0].image_url}
                            alt={category.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-100 to-zinc-50">
                            <Package className="w-20 h-20 text-zinc-300" />
                          </div>
                        )}
                        {/* Subtle Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                        
                        {/* Minimal Badge */}
                        <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm text-black px-3 py-1.5 rounded-md text-xs font-semibold border border-zinc-200">
                          {category.products.length} {category.products.length === 1 ? 'Item' : 'Items'}
                        </div>
                      </div>

                      {/* Content - Minimal Typography */}
                      <CardContent className="p-8">
                        <h3 className="text-xl font-semibold text-black mb-3 group-hover:text-yellow-600 transition-colors duration-300 leading-snug">
                          {category.name}
                        </h3>
                        <p className="text-zinc-500 text-sm leading-relaxed mb-6">
                          {category.products[0]?.description?.slice(0, 80) || 'Premium quality industrial equipment'}
                          {category.products[0]?.description?.length > 80 ? '...' : ''}
                        </p>

                        {/* Minimal CTA */}
                        <div className="flex items-center text-yellow-600 group-hover:text-yellow-700 font-medium text-sm group/cta">
                          <span>View Products</span>
                          <ArrowRight className="ml-2 w-4 h-4 group-hover/cta:translate-x-1 transition-transform duration-300" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>

            {/* Bottom Info - Minimal */}
            <div className="mt-20 text-center">
              <div className="inline-flex items-center gap-3 bg-white border border-zinc-200 rounded-full px-6 py-3.5 shadow-sm">
                <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                <span className="text-zinc-600 text-sm">
                  All products available for bulk orders • Contact us for quotations
                </span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Product Details Modal */}
      <Dialog open={!!selectedCategory} onOpenChange={() => setSelectedCategory(null)}>
        <DialogContent className="bg-white border-2 border-zinc-200 text-black max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-yellow-600">
              {selectedCategory?.name}
            </DialogTitle>
            <DialogDescription className="text-zinc-600">
              Browse our complete range of {selectedCategory?.name.toLowerCase()}
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-6">
            <h4 className="text-lg font-semibold text-black mb-4">Available Products:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {selectedCategory?.products.map((product, index) => (
                <div
                  key={index}
                  className="flex gap-4 p-4 bg-zinc-50 rounded-lg border-2 border-zinc-200 hover:border-yellow-500 transition-colors cursor-pointer"
                  onClick={() => setSelectedProduct(product)}
                >
                  {product.image_url ? (
                    <img 
                      src={product.image_url} 
                      alt={product.name}
                      className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                    />
                  ) : (
                    <div className="w-20 h-20 bg-zinc-200 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Package className="w-8 h-8 text-zinc-400" />
                    </div>
                  )}
                  <div className="flex-1">
                    <h5 className="font-bold text-black mb-1">{product.name}</h5>
                    <p className="text-xs text-zinc-600 line-clamp-2">{product.description}</p>
                    {product.specifications.length > 0 && (
                      <p className="text-xs text-yellow-600 mt-1">
                        {product.specifications.length} specifications
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-6 p-4 bg-yellow-50 border-2 border-yellow-500 rounded-lg">
              <p className="text-zinc-700 text-sm">
                <strong className="text-yellow-700">Note:</strong> For detailed specifications, pricing, and bulk orders, 
                please contact our sales team or download our complete product catalog.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Individual Product Modal */}
      <Dialog open={!!selectedProduct} onOpenChange={() => setSelectedProduct(null)}>
        <DialogContent className="bg-white border-2 border-zinc-200 text-black max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-black">
              {selectedProduct?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="mt-4">
            {selectedProduct?.image_url && (
              <div className="mb-6">
                <img 
                  src={selectedProduct.image_url} 
                  alt={selectedProduct.name}
                  className="w-full h-64 object-contain bg-zinc-50 rounded-lg"
                />
              </div>
            )}

            <div className="mb-6">
              <h4 className="font-semibold text-black mb-2">Description:</h4>
              <p className="text-zinc-700">{selectedProduct?.description}</p>
            </div>

            {selectedProduct?.specifications && selectedProduct.specifications.length > 0 && (
              <div>
                <h4 className="font-semibold text-black mb-3">Specifications:</h4>
                <ul className="space-y-2">
                  {selectedProduct.specifications.map((spec, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-zinc-700">
                      <span className="text-yellow-500 mt-1">✓</span>
                      <span>{spec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-zinc-700">
                <strong className="text-yellow-700">Interested in this product?</strong><br />
                Contact us for pricing, availability, and bulk orders.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default ProductsSection;
